// src/components/MetricsDisplay.tsx
import React from 'react';
import BettingMetricCard from './BettingMetricCard';
import {
  TimeSeriesDataPoint,
  D3ChartConfig,
  EVHistoryEntry,
  LiquidityHistoryEntry,
} from '../types';
import * as d3 from 'd3';

interface MetricsDisplayProps {
  // Lay Odds Metric
  layOddsValue: string;
  layEVPercentage: number | null;
  layEVClass?: string;

  // Midpoint Odds Metric
  midpointOddsValue: string;
  midpointEVPercentage: number | null;
  midpointEVClass?: string;

  // Liquidity Metric
  totalLiquidityValue: string;
  liquiditySymbol: string;

  // Adjusted Back Odds Metric
  adjustedBackOddsValue?: string;
  adjustedBackEVPercentage?: number | null;
  adjustedBackEVClass?: string;
  applyBackOddsPercentage?: boolean;
  backOddsPercentageValue?: number; // Added to display in title
  backOddsHistory?: TimeSeriesDataPoint[];
  backOddsTimeScale?: string;

  // Common Chart Data
  evHistory: TimeSeriesDataPoint[];
  liquidityHistory: TimeSeriesDataPoint[];

  // Common Chart Scales & Flags
  evTimeScale: string;
  liquidityTimeScale: string;
  isLive: boolean;
  areChartsVisible: boolean;
  baseChartId: string;

  isLoading?: boolean;
}

const MetricsDisplay: React.FC<MetricsDisplayProps> = ({
  layOddsValue,
  layEVPercentage,
  layEVClass,
  midpointOddsValue,
  midpointEVPercentage,
  midpointEVClass,
  totalLiquidityValue,
  liquiditySymbol,
  adjustedBackOddsValue,
  adjustedBackEVPercentage,
  adjustedBackEVClass,
  applyBackOddsPercentage,
  backOddsPercentageValue, // Added
  backOddsHistory,
  backOddsTimeScale,
  evHistory,
  liquidityHistory,
  evTimeScale,
  liquidityTimeScale,
  isLive,
  areChartsVisible,
  baseChartId,
  isLoading = false,
}) => {
  const layEVChartConfig: D3ChartConfig = {
    lineColorVar: '#2A7B9B', // Blue color
    pointFillVar: '#2A7B9B',
    tooltipFormatter: (d: any) =>
      `${(d as EVHistoryEntry).layEV}% at ${new Date(d.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
    dataKey: 'layEV',
  };

  const midpointEVChartConfig: D3ChartConfig = {
    lineColorVar: '#EDDD53', // Yellow color
    pointFillVar: '#EDDD53',
    tooltipFormatter: (d: any) =>
      `${(d as EVHistoryEntry).midpointEV}% at ${new Date(d.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
    dataKey: 'midpointEV',
  };

  const liquidityChartConfig: D3ChartConfig = {
    lineColorVar: '#57C785', // Green color
    pointFillVar: '#57C785',
    yAxisFormatter: (dValue) => `$${d3.format(',.0f')(dValue as number)}`,
    tooltipFormatter: (d: any) =>
      `$${d3.format(',.0f')((d as LiquidityHistoryEntry).total)} at ${new Date(d.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
    dataKey: 'total',
  };

  const getStatisticClassName = (evClass?: string): string => {
    if (evClass === 'positive') return 'text-emerald-500';
    if (evClass === 'negative') return 'text-red-500';
    return 'text-text-secondary-light dark:text-text-secondary-dark';
  };

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 p-1'>
      <BettingMetricCard
        title='Lay Odds'
        value={layOddsValue}
        isLoading={isLoading}
        statistic={layEVPercentage}
        statisticPrefix={
          layEVPercentage !== null && layEVPercentage > 0 ? '+' : ''
        }
        statisticSuffix={layEVPercentage !== null ? '% EV' : ''}
        statisticClassName={getStatisticClassName(layEVClass)}
        chartId={`${baseChartId}-lay-ev-chart`}
        chartData={evHistory}
        chartConfig={layEVChartConfig}
        chartTimeScale={evTimeScale}
        isLive={isLive}
        areChartsVisible={areChartsVisible}
        applyBackOddsPercentage={false}
      />
      <BettingMetricCard
        title='Midpoint Odds'
        value={midpointOddsValue}
        isLoading={isLoading}
        statistic={midpointEVPercentage}
        statisticPrefix={
          midpointEVPercentage !== null && midpointEVPercentage > 0 ? '+' : ''
        }
        statisticSuffix={midpointEVPercentage !== null ? '% EV' : ''}
        statisticClassName={getStatisticClassName(midpointEVClass)}
        chartId={`${baseChartId}-midpoint-ev-chart`}
        chartData={evHistory}
        chartConfig={midpointEVChartConfig}
        chartTimeScale={evTimeScale}
        isLive={isLive}
        areChartsVisible={areChartsVisible}
        applyBackOddsPercentage={false}
      />
      <BettingMetricCard
        title='Total Liquidity'
        value={totalLiquidityValue}
        isLoading={isLoading}
        chartId={`${baseChartId}-liquidity-chart`}
        chartData={liquidityHistory}
        chartConfig={liquidityChartConfig}
        chartTimeScale={liquidityTimeScale}
        isLive={isLive}
        areChartsVisible={areChartsVisible}
        applyBackOddsPercentage={false}
      />
    </div>
  );
};

export default MetricsDisplay;
